import { SIM_PROCESSION_DAYS, type SimBrotherhoodProblem, type SimCareerView } from '@cruz/shared';
import { useState, type FormEvent } from 'react';
import { simApi, SimApiError, simErrorMessage } from './api.ts';
import { Portrait } from './Cast.tsx';
import { brotherhoodName, num, relationLabel, upperFirst } from './format.ts';
import { card, inputClass, Notice, PageTitle, primaryButton, Working } from './ui.tsx';

/** Founding the player's brotherhood, and meeting the people of the game. */

const EXAMPLE = {
  name: 'Hermandad del Santísimo Cristo de la Paciencia y María Santísima del Buen Fin',
  popular_name: 'la Paciencia',
  church: 'Parroquia de San Prudencio',
};

type Field = 'name' | 'popular_name' | 'day' | 'church';

export function FoundScreen({
  again,
  onDone,
}: {
  /** A finished career before: start another one */
  again: boolean;
  onDone: (career: SimCareerView) => void;
}) {
  const [name, setName] = useState('');
  const [popular, setPopular] = useState('');
  const [day, setDay] = useState('');
  const [church, setChurch] = useState('');
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState<SimBrotherhoodProblem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const problemOf = (f: Field) => problems.find((p) => p.field === f)?.message ?? null;
  const general = problems.find((p) => p.field === 'all')?.message ?? null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const missing: SimBrotherhoodProblem[] = [];
    if (!name.trim())
      missing.push({ field: 'name', reason: 'required', message: 'Ponle nombre a tu hermandad.' });
    if (!day)
      missing.push({ field: 'day', reason: 'required', message: 'Elige el día de salida.' });
    if (!church.trim())
      missing.push({
        field: 'church',
        reason: 'required',
        message: '¿De qué iglesia o capilla sale?',
      });
    setProblems(missing);
    if (missing.length) return;
    setBusy(true);
    setError(null);
    try {
      onDone(await simApi.createCareer({ name, popular_name: popular || null, day, church }));
    } catch (err) {
      if (err instanceof SimApiError && err.body?.problems) setProblems(err.body.problems);
      else setError(simErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const fieldProps = (f: Field) => {
    const message = problemOf(f);
    return {
      'aria-invalid': message ? true : undefined,
      'aria-describedby': message ? `found-${f}-error` : `found-${f}-hint`,
    };
  };
  const fieldError = (f: Field) => {
    const message = problemOf(f);
    return message ? (
      <p
        id={`found-${f}-error`}
        className="mt-1.5 text-sm font-medium text-burgundy dark:text-burgundy-300"
      >
        {message}
      </p>
    ) : null;
  };

  return (
    <div>
      <PageTitle kicker={again ? 'Una carrera nueva' : 'Tu carrera empieza aquí'}>
        Funda tu hermandad
      </PageTitle>
      <p className="mb-5 text-ink-soft dark:text-cream/80">
        Es tuya: inventa el nombre, el día y la iglesia. Tiene que ser inventada; las de verdad no
        valen.
      </p>
      <form className="space-y-5" onSubmit={(e) => void submit(e)} noValidate>
        <div>
          <label htmlFor="found-name" className="mb-1.5 block text-base font-semibold">
            Nombre de la hermandad
          </label>
          <textarea
            id="found-name"
            className={`${inputClass} min-h-24 resize-none`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            placeholder={EXAMPLE.name}
            {...fieldProps('name')}
          />
          <p id="found-name-hint" className="sr-only">
            Por ejemplo: {EXAMPLE.name}
          </p>
          {fieldError('name')}
        </div>
        <div>
          <label htmlFor="found-popular_name" className="mb-1.5 block text-base font-semibold">
            Nombre popular{' '}
            <span className="font-normal text-ink-soft dark:text-cream/70">(opcional)</span>
          </label>
          <input
            id="found-popular_name"
            className={inputClass}
            value={popular}
            onChange={(e) => setPopular(e.target.value)}
            maxLength={80}
            placeholder={EXAMPLE.popular_name}
            {...fieldProps('popular_name')}
          />
          <p
            id="found-popular_name-hint"
            className="mt-1.5 text-sm text-ink-soft dark:text-cream/70"
          >
            Como la llama la gente del barrio.
          </p>
          {fieldError('popular_name')}
        </div>
        <div>
          <label htmlFor="found-day" className="mb-1.5 block text-base font-semibold">
            Día de salida
          </label>
          <select
            id="found-day"
            className={inputClass}
            value={day}
            onChange={(e) => setDay(e.target.value)}
            {...fieldProps('day')}
          >
            <option value="">Elige un día…</option>
            {SIM_PROCESSION_DAYS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          <p id="found-day-hint" className="sr-only">
            El día de la Semana Santa en que sale.
          </p>
          {fieldError('day')}
        </div>
        <div>
          <label htmlFor="found-church" className="mb-1.5 block text-base font-semibold">
            Iglesia o capilla de salida
          </label>
          <input
            id="found-church"
            className={inputClass}
            value={church}
            onChange={(e) => setChurch(e.target.value)}
            maxLength={120}
            placeholder={EXAMPLE.church}
            {...fieldProps('church')}
          />
          <p id="found-church-hint" className="sr-only">
            Por ejemplo: {EXAMPLE.church}
          </p>
          {fieldError('church')}
        </div>
        {general && <Notice tone="error">{general}</Notice>}
        {error && <Notice tone="error">{error}</Notice>}
        {busy ? (
          <Working label="Revisando tu hermandad y buscando hermanos…" />
        ) : (
          <button type="submit" className={primaryButton}>
            Fundar la hermandad
          </button>
        )}
      </form>
    </div>
  );
}

const ROLE_ORDER = [
  'chief',
  'tramo_deputy',
  'priosta',
  'capataz',
  'friend',
  'grandmother',
  'marchas',
  'neighbor',
  'rival',
];

/** After founding: the brotherhood and its people, then the first day. */
export function IntroScreen({ career, onStart }: { career: SimCareerView; onStart: () => void }) {
  const b = career.brotherhood;
  const people = [...career.characters]
    .filter((c) => c.role !== 'secondary')
    .sort((a, z) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(z.role));
  return (
    <div className="space-y-6">
      <div>
        <PageTitle kicker="Ya eres hermano">{upperFirst(brotherhoodName(b))}</PageTitle>
        <p className="text-lg leading-relaxed">
          {b.popular_name && <>{b.name}. </>}Sale el <strong>{b.day}</strong> desde {b.church}. Sois{' '}
          {num(b.members)} hermanos, y tú eres el número{' '}
          <strong>{num(career.player.member_number)}</strong>: el último. Este año te toca cirio en
          el tramo {career.player.tramo}, el más lejos del paso.
        </p>
      </div>
      <section>
        <h2 className="mb-3 font-serif text-2xl font-semibold text-purple dark:text-gold-300">
          La gente de tu hermandad
        </h2>
        <ul className="space-y-2">
          {people.map((c) => (
            <li key={c.id} className={`${card} flex items-center gap-4 px-4 py-3`}>
              <Portrait id={c.id} name={c.name} src={c.portrait} size={64} />
              <div className="min-w-0">
                <p className="text-lg font-semibold">{c.name}</p>
                <p className="text-ink-soft dark:text-cream/75">
                  {upperFirst(c.description)}
                  {c.relation !== 0 && ` · ${relationLabel(c.relation).toLowerCase()}`}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </section>
      <button type="button" className={primaryButton} onClick={onStart}>
        Empezar mi primer día
      </button>
    </div>
  );
}
